import { useCallback, useEffect, useState } from 'react';
import { FileRejection } from 'react-dropzone';

import { useFormikContext } from 'formik';

import { postMedia } from '@common/api/media';

import { FileUploadErrorsCodes } from '../constants/types';

type PropsType = {
	name: string;
};

const useSingleFileDrop = ({ name }: PropsType) => {
	const { setFieldValue } = useFormikContext<any>();
	const [file, setFile] = useState<File | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [isLoading, setIsLoading] = useState(false);

	const onDrop = useCallback((acceptedFiles: File[]) => {
		setError(null);
		if (acceptedFiles.length > 0) {
			setFile(acceptedFiles[0]);
		}
	}, []);

	const onReject = (rejectedFiles: FileRejection[], maxSize?: number) => {
		const [file] = rejectedFiles;
		const errorCode = file?.errors?.[0].code;

		if (maxSize && errorCode === FileUploadErrorsCodes.LargeFile) {
			setError(`File is more than ${maxSize}mb`);
		} else if (errorCode === FileUploadErrorsCodes.InvalidType) {
			setError(`${file.file.type} type is not supported.`);
		} else {
			setError(`${file?.errors?.[0]?.message}`);
		}
	};

	const uploadFile = async (file: File) => {
		setIsLoading(true);

		try {
			const response = await postMedia(file);

			if (response.url) {
				setFieldValue(name, response.url);
			} else {
				setError('Something went wrong');
			}
		} catch (error) {
			setError(error.message);
		} finally {
			setIsLoading(false);
		}
	};

	useEffect(() => {
		if (file) {
			uploadFile(file);
		}
	}, [file]);

	return {
		onDrop,
		file,
		onReject,
		error,
		isLoading,
	};
};

export default useSingleFileDrop;
